import { ChangeEvent, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Upload } from "lucide-react";
import type { ImportBatchInput, Shop, ShopCreateInput } from "../../shops/types";
import {
  autoMapHeaders,
  buildImportPreview,
  IMPORT_FIELDS,
  parseSpreadsheetFile,
  type ImportColumnMapping,
  type ParsedSheet
} from "../parsers/shopImportParser";

type ImportShopsProps = {
  existingShops: Shop[];
  onImportShops: (shops: ShopCreateInput[], batch: ImportBatchInput) => Promise<unknown>;
};

export function ImportShops({ existingShops, onImportShops }: ImportShopsProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedSheet, setParsedSheet] = useState<ParsedSheet | null>(null);
  const [mapping, setMapping] = useState<ImportColumnMapping>({});
  const [error, setError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const preview = useMemo(() => {
    if (!parsedSheet) {
      return null;
    }

    return buildImportPreview(parsedSheet, mapping, existingShops);
  }, [existingShops, mapping, parsedSheet]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFile(selectedFile);
    setParsedSheet(null);
    setMapping({});
    setError(null);
    setSuccessMessage(null);

    if (!selectedFile) {
      return;
    }

    setIsParsing(true);

    try {
      const parsed = await parseSpreadsheetFile(selectedFile);
      setParsedSheet(parsed);
      setMapping(autoMapHeaders(parsed.headers));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to read the import file.");
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = async () => {
    if (!file || !parsedSheet || !preview || preview.validShops.length === 0) {
      return;
    }

    setIsImporting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await onImportShops(preview.validShops, {
        fileName: file.name,
        totalRows: parsedSheet.rows.length,
        importedRows: preview.validShops.length,
        rejectedRows: preview.rejectedRows
      });
      setSuccessMessage(`Imported ${preview.validShops.length} shops from ${file.name}.`);
      setFile(null);
      setParsedSheet(null);
      setMapping({});
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to import shops.");
    } finally {
      setIsImporting(false);
    }
  };

  const updateMapping = (field: keyof ImportColumnMapping, value: string) => {
    setMapping((current) => ({
      ...current,
      [field]: value === "" ? "" : Number(value)
    }));
  };

  return (
    <section className="import-panel" aria-label="Import shops">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Excel import</p>
          <h2>Import cosmetic shops</h2>
        </div>
      </div>

      <label className="drop-zone">
        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
        <FileSpreadsheet size={28} />
        <strong>{file ? file.name : "Choose Excel or CSV file"}</strong>
        <span>Rows only need a shop name. Other columns are optional.</span>
      </label>

      {isParsing ? <div className="empty-state compact-state">Reading file...</div> : null}
      {error ? <div className="alert error-alert">{error}</div> : null}
      {successMessage ? (
        <div className="alert success-alert">
          <CheckCircle2 size={17} />
          {successMessage}
        </div>
      ) : null}

      {parsedSheet ? (
        <>
          <div className="mapping-panel">
            <div className="section-heading small-heading">
              <div>
                <p className="eyebrow">Column mapping</p>
                <h3>Confirm imported fields</h3>
              </div>
            </div>

            <div className="mapping-grid">
              {IMPORT_FIELDS.map((option) => (
                <label key={option.field}>
                  <span>
                    {option.label}
                    {option.required ? " *" : ""}
                  </span>
                  <select
                    value={mapping[option.field] ?? ""}
                    onChange={(event) => updateMapping(option.field, event.target.value)}
                  >
                    <option value="">Not mapped</option>
                    {parsedSheet.headers.map((header, index) => (
                      <option key={`${header}-${index}`} value={index}>
                        {header}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>

          {preview ? (
            <div className="preview-panel">
              <div className="import-summary">
                <span>{preview.validShops.length} ready</span>
                <span>{preview.rejectedRows} rejected</span>
                <span>{preview.warningCount} warnings</span>
              </div>

              <div className="table-wrap preview-table">
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Shop name</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 12).map((row) => {
                      const hasError = row.issues.some((issue) => issue.severity === "error");
                      const warnings = row.issues.filter((issue) => issue.severity === "warning");

                      return (
                        <tr key={row.rowNumber}>
                          <td>{row.rowNumber}</td>
                          <td>{row.shopName ?? "-"}</td>
                          <td>
                            {hasError ? (
                              <span className="status-badge missing">Rejected</span>
                            ) : warnings.length ? (
                              <span className="status-badge text-only">Warning</span>
                            ) : (
                              <span className="status-badge exact">Ready</span>
                            )}
                          </td>
                          <td>
                            {row.issues.length ? (
                              <div className="issue-list">
                                {row.issues.map((issue) => (
                                  <span key={`${issue.field}-${issue.message}`}>
                                    {issue.severity === "warning" ? <AlertTriangle size={13} /> : null}
                                    {issue.message}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              "No issues"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {preview.rows.length > 12 ? (
                <p className="muted-note">Showing first 12 rows from {preview.rows.length} detected rows.</p>
              ) : null}

              <button
                type="button"
                className="primary-button"
                disabled={preview.validShops.length === 0 || isImporting}
                onClick={handleImport}
              >
                <Upload size={17} />
                {isImporting ? "Importing..." : `Import ${preview.validShops.length} shops`}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
